import React from "react";
import { v5 } from "@credijusto/minerals";
import styles from "./SiteHeaderV3.module.css";
import getDynamicLink from "../../utils/getDynamicLink";
import menu from "./data.json";
import { v4 as uuidv4 } from "uuid";
import LogoCovalto from "../../assets/images/LogoCovalto.png";
import LogoCovaltoPhone from "../../assets/images/LogoCovaltoPhone.png";

// Componente para el botón de acción
const BtnAction = ({ name, slug }) => {
  const handleClick = () => {
    const btn = document.getElementById("btnNavCTA");
    btn?.blur();
    btn?.classList.add(styles.btn);
  };

  const handleMouseEnter = () => {
    const btn = document.getElementById("btnNavCTA");
    btn?.classList.remove(styles.btn);
  };

  return (
    <v5.Button
      id="btnNavCTA"
      as="a"
      href={slug}
      target="_blank"
      variant="secondary"
      className={`${styles.VirtualButton} whitespace-nowrap text-mn-body2 leading-mn-body2 tracking-mn-body2 text-mn-text-primary`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      {name}
    </v5.Button>
  );
};

// Componente para el logo responsivo (actualizado)
const ResponsiveLogo = () => (
  <a
    href="https://www.covalto.com/"
    onClick={(e) => {
      e.preventDefault();
      window.location.href = "https://www.covalto.com/";
    }}
    style={{ display: "contents" }}
  >
    <img
      src={LogoCovalto}
      alt="Covalto"
      className={`${styles.logo} ${styles.logoDesktop}`}
    />
    <img
      src={LogoCovaltoPhone}
      alt="Covalto"
      className={`${styles.logo} ${styles.logoMobile}`}
    />
  </a>
);

// Componente principal del Nav
const NavComponent = ({
  handleMegamenuOverview = () => {},
  handleMegamenuProducts = () => {},
  showMegamenuProducts = false,
  showMegamenuOverview = false,
}) => {
  const eventMegaMenuProducts = (elements) => {
    handleMegamenuProducts(elements, menu.subtitle);
  };

  // Función para consolidar ítems duplicados
  const consolidateDuplicateItems = (items) => {
    const consolidated = {};

    items.forEach((item) => {
      if (item.title === "Inversión" || item.title === "Cuenta") {
        if (!consolidated[item.title]) {
          consolidated[item.title] = {
            ...item,
            products: [
              ...new Map(
                item.products.map((link) => [link.slug, link])
              ).values(),
            ],
          };
        } else {
          const existingLinks = new Set(
            consolidated[item.title].products.map((l) => l.slug)
          );
          const newLinks = item.products.filter(
            (link) => !existingLinks.has(link.slug)
          );
          consolidated[item.title].products.push(...newLinks);
        }
      } else {
        const key = `${item.title}-${item.orderMobile}`;
        if (!consolidated[key]) {
          consolidated[key] = item;
        }
      }
    });

    return Object.values(consolidated);
  };

  // Procesar menuDesktop
  const menuDesktop = menu.menuElements.map((item, index) => ({
    name: item.title,
    type:
      item.strapi_component === "nav-header.menu-element-link"
        ? "link"
        : item.strapi_component === "nav-header.mega-menu-products"
        ? "megamenu-productos"
        : "megamenu-overview",
    icon: item.icon?.url || null,
    slug: item.slug ? getDynamicLink(item.slug) : null,
    data: item,
    key: `desktop-${item.id || index}`,
  }));

  // Procesar productos del menú
  const productsMenu = consolidateDuplicateItems(
    menu.menuElements
      .filter(
        (item) => item.strapi_component === "nav-header.mega-menu-products"
      )
      .flatMap(
        (item) =>
          item.groups?.flatMap(
            (group) =>
              group.products?.map((product) => ({
                title: product.title,
                orderMobile: product.orderMobile,
                products:
                  product.links?.map((link) => ({
                    name: link.name,
                    slug: link.slug,
                    new: link.new,
                    key: `link-${link.id || uuidv4()}`,
                  })) || [],
                key: `product-${product.id || uuidv4()}`,
              })) || []
          ) || []
      )
  );

  // Procesar menú "Sobre Covalto"
  const aboutMenu = menu.menuElements
    .filter(
      (item) =>
        item.strapi_component === "nav-header.menu-element-sobre-covalto"
    )
    .flatMap(
      (item) =>
        item.columnItems?.map((column) => ({
          title: column.columnName,
          products:
            column.links?.map((link) => ({
              name: link.name,
              slug: link.slug,
              key: `about-link-${link.id || uuidv4()}`,
            })) || [],
          orderMobile: column.orderMobile,
          key: `about-col-${column.id || uuidv4()}`,
        })) || []
    );

  // Construir menú móvil
  const menuMobile = {
    subtitle: menu.subtitle,
    cards: menu.menuElements
      .filter(
        (item) => item.strapi_component === "nav-header.menu-element-link"
      )
      .map((item, index) => ({
        name: item.title,
        slug: getDynamicLink(item.slug),
        type: "link",
        icon: item.icon?.url || null,
        key: `mobile-card-${item.id || index}`,
      })),
    menu: [
      ...productsMenu,
      ...aboutMenu,
      ...(menu.menuElements.some((item) => item.blogTitle)
        ? [
            {
              title: "Blog",
              products: [
                {
                  slug: "/blog/",
                  name: "Visitar el blog",
                  icon: "https://covalto-production-website.s3.amazonaws.com/Group_168b08b696.svg",
                  key: "blog-link",
                },
              ],
              key: "blog-section",
            },
          ]
        : []),
    ].sort((a, b) => (a.orderMobile || 0) - (b.orderMobile || 0)),
  };

  return (
    <v5.Nav
      logo={<ResponsiveLogo />}
      sticky={true}
      btnAction={
        <BtnAction slug={menu.ctaButtonSlug} name={menu.ctaButtonTitle} />
      }
      asLinkElements="a"
      menuDesktop={menuDesktop}
      menuMobile={menuMobile}
      handleMegamenuOverview={handleMegamenuOverview}
      handleMegamenuProducts={eventMegaMenuProducts}
      isMegamenuProductsOpen={showMegamenuProducts}
      isMegamenuOverviewOpen={showMegamenuOverview}
      className={styles.nav}
    />
  );
};

export default NavComponent;
